import { ProductFilterInput, ProductFilterOperator } from '@app-domains/meilisearch/types/product-search.types'

type FacetFilter = string | string[]

export function mapFacetFilters(facetFilters: FacetFilter[]): ProductFilterInput[] {
    const filters: ProductFilterInput[] = []

    for (const group of facetFilters) {
        if (typeof group === 'string') {
            const parsed = parseFacetEntry(group)

            if (parsed !== null) {
                filters.push(buildFacetFilter(parsed))
            }

            continue
        }

        const entries = group
        const byAttribute = new Map<string, { negated: boolean; values: string[] }>()

        for (const entry of entries) {
            const parsed = parseFacetEntry(entry)

            if (parsed === null) {
                continue
            }

            const mapKey = `${parsed.attribute}:${parsed.negated ? 'neq' : 'eq'}`
            const bucket = byAttribute.get(mapKey) ?? { negated: parsed.negated, values: [] }
            bucket.values.push(...parsed.values)
            byAttribute.set(mapKey, bucket)
        }

        byAttribute.forEach((bucket, mapKey) => {
            const attribute = mapKey.replace(/:(eq|neq)$/, '')
            filters.push(buildFacetFilter({
                attribute,
                negated: bucket.negated,
                values: [...new Set(bucket.values)],
            }))
        })
    }

    return filters
}

export function normalizeFacetFilters(facetFilters: FacetFilter | FacetFilter[] | undefined): FacetFilter[] {
    if (facetFilters == null) {
        return []
    }

    return Array.isArray(facetFilters) ? facetFilters : [facetFilters]
}

function buildFacetFilter(parsed: {
    attribute: string
    negated: boolean
    values: string[]
}): ProductFilterInput {
    const operator: ProductFilterOperator = parsed.negated
        ? 'NEQ'
        : parsed.values.length > 1 ? 'IN' : 'EQ'

    return {
        attribute: parsed.attribute,
        operator,
        values: parsed.values,
    }
}

function parseFacetEntry(entry: string): {
    attribute: string
    negated: boolean
    values: string[]
} | null {
    const negatedMatch = entry.match(/^(.+):-(.+)$/)

    if (negatedMatch) {
        return {
            attribute: negatedMatch[1],
            negated: true,
            values: [negatedMatch[2]],
        }
    }

    const match = entry.match(/^(.+):(.+)$/)

    if (! match) {
        return null
    }

    return {
        attribute: match[1],
        negated: false,
        values: [match[2]],
    }
}
